import { useEffect } from "react";
import StockChart from "./StockChart";
import { formatDay } from "../../../services/utils";
import SortFilterTable, { AlignOptions } from "../../common/SortFilterTable";
import DatePickerRange from "../../common/DatePickerRange";
import { useDispatch, useSelector } from "react-redux";
import { ThunkDispatch } from 'redux-thunk';
import { AnyAction } from 'redux';
import { RootState } from '../../../redux-store/reducers';
import { requestPublicOpenClosingPrice } from "../../../redux-store/actions/publicActions";
import { setDayRange } from "../../../redux-store/actions/dashboardActions";
// Service imports:
import { formatNumber } from '../../../services/utils';
import { useParams } from "react-router-dom";

interface SigtiRangeHistoryProps {
  symbol: string;
}

interface StockData {
    symbol: string;
    market_symbol: string;
    market_name: string;
    exchange_symbol: string;
    exchange_name: string;
    trading_date: string;
    open: number | null;
    open_yield: number | null;
    high: number | null;
    high_yield: number | null;
    low: number | null;
    low_yield: number | null;
    close: number;
    official_last: number;
    official_last_corrected: number;
    official_last_yield_corrected: number | null;
    close_yield: number | null;
    volume: number;
    turnover: number;
    number_of_trades: number;
    number_of_alerts: number | null;
  }

const SigtiRangeHistory = ({ symbol }: SigtiRangeHistoryProps) => {
  const dispatch: ThunkDispatch<RootState, void, AnyAction> = useDispatch();

  const fromDate = useSelector((state: any) => state.dashboardReducer.dayRange.fromDate);
  const toDate = useSelector((state: any) => state.dashboardReducer.dayRange.toDate);
  const publicOpenClosingPrice = useSelector((state: any) => state.publicReducer.publicOpenClosingPrice);

  useEffect(() => {
    // get one year range from today
    const today = new Date();
    const startDate = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 365);
    const dateToDay = new Date();
  
    dispatch(
      setDayRange(
        startDate.toISOString().split('T')[0],
        dateToDay.toISOString().split('T')[0],
        true
      )
    );
  }, [dispatch]);

  useEffect(() => {
    if (fromDate && toDate) {
      const fetchData = async () => {
        try {
          dispatch(requestPublicOpenClosingPrice(symbol, fromDate, toDate));
        } catch (error) {
          console.error("Error fetching data:", error);
        }
      };

      fetchData();
    }
  }, [fromDate, toDate, dispatch, symbol]);

  const dummyData = publicOpenClosingPrice[symbol]?.[fromDate]?.[toDate];
  if (!dummyData) {
    return null;
  }
  if (dummyData === 'waiting') {
    return <div>Loading...</div>;
  }
  if (dummyData?.message) {
    return <div>{dummyData.message}</div>;
  }
  if (!Array.isArray(dummyData) || dummyData.length === 0) {
    return <div>No data available for the selected range.</div>;
  }

  const columns = [
    {
      title: 'Date',
      dataAccessor: ({ trading_date }: StockData) =>  formatDay(trading_date),
      cellClassName: 'SigtiRangeHistory-tr',
      textAlign: 'left' as AlignOptions,
    },
    {
      title: 'Open',
      dataAccessor: ({ open }: StockData) =>  formatNumber(open),
      cellClassName: 'SigtiRangeHistory-tr right',
      textAlign: 'right' as AlignOptions,
    },
    {
      title: 'High',
      dataAccessor: ({ high }: StockData) =>  formatNumber(high),
      cellClassName: 'SigtiRangeHistory-tr right',
      textAlign: 'right' as AlignOptions,
    },
    {
      title: 'Low',
      dataAccessor: ({ low }: StockData) =>  formatNumber(low),
      cellClassName: 'SigtiRangeHistory-tr right',
      textAlign: 'right' as AlignOptions,
    },
    {
      title: 'Close',
      dataAccessor: ({ close }: StockData) =>  formatNumber(close),
      cellClassName: 'SigtiRangeHistory-tr right',
      textAlign: 'right' as AlignOptions,
    },
    {
      title: 'Volume',
      dataAccessor: ({ volume }: StockData) =>  formatNumber(volume, 'volume'),
      cellClassName: 'SigtiRangeHistory-tr right',
      textAlign: 'right' as AlignOptions,
    },
    {
      title: 'Turnover',
      dataAccessor: ({ turnover }: StockData) =>  formatNumber(turnover, 'volume'),
      cellClassName: 'SigtiRangeHistory-tr right',
      textAlign: 'right' as AlignOptions,
    },
    {
      title: 'Number of Trades',
      dataAccessor: ({ number_of_trades }: StockData) =>  number_of_trades,
      cellClassName: 'SigtiRangeHistory-tr right',
      textAlign: 'right' as AlignOptions,
    },
    {
      title: 'Number of Alerts',
      dataAccessor: ({ number_of_alerts }: StockData) =>  number_of_alerts,
      cellClassName: 'SigtiRangeHistory-tr right',
      textAlign: 'right' as AlignOptions,
    }
  ];

  return (
    <div className="sigti-range-history">
      <DatePickerRange  />
      <StockChart data={dummyData} />
      <SortFilterTable columns={columns} data={dummyData} isLoading={false} isError={false} />
    </div>
  );
};

const SigtiRangeHistoryWrapper = () => {
  const { symbol = '' } = useParams();
  return <SigtiRangeHistory symbol={symbol} />;
};

export default SigtiRangeHistoryWrapper;
